import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import BackendApiService from '../services/BackendApiService';
import { getAccessToken } from '../utils/accessTokenStore';

interface MaintenanceConfig {
  enabled: boolean;
  title: string;
  description: string;
}

interface MaintenanceContextType {
  maintenanceConfig: MaintenanceConfig;
  isMaintenanceMode: boolean;
}

const MaintenanceContext = createContext<MaintenanceContextType | undefined>(undefined);

export const useMaintenanceMode = () => {
  const context = useContext(MaintenanceContext);
  if (!context) {
    throw new Error('useMaintenanceMode must be used within a MaintenanceProvider');
  }
  return context;
};

interface MaintenanceProviderProps {
  children: ReactNode;
}

export const MaintenanceProvider: React.FC<MaintenanceProviderProps> = ({ children }) => {
  const [maintenanceConfig, setMaintenanceConfig] = useState<MaintenanceConfig>({
    enabled: false,
    title: '',
    description: ''
  });

  useEffect(() => {
    const fetchMaintenanceConfig = async () => {
      try {
        const accessToken = getAccessToken();
        const backendApiService = new BackendApiService(accessToken ?? '');
        const config = await backendApiService.getMaintenanceConfig();
        setMaintenanceConfig(config);
      } catch (err) {
        console.log('Failed to fetch maintenance config:', err);
      }
    };

    fetchMaintenanceConfig();
  }, []);

  const value = {
    maintenanceConfig,
    isMaintenanceMode: maintenanceConfig.enabled
  };

  return (
    <MaintenanceContext.Provider value={value}>
      {children}
    </MaintenanceContext.Provider>
  );
};
